import React from 'react';
import { Content, Root as DropdownRoot, Portal, Trigger } from '@radix-ui/react-dropdown-menu';
import { List, Root } from '@radix-ui/react-navigation-menu';
import Router, { useRouter } from 'next/router';
import { useGate } from 'statsig-react';
import { styled } from '@components-shared/src/stitches';
import { UserInfo } from '@components/Header/NavMenu/UserInfo';
import { TotalPointsBadge } from '@components/Points/TotalPointsBadge';
import { Button } from '@components/main-components/button/Button';
import { Divider } from '@components/main-components/divider';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { useAuth } from '@context/auth/base';
import { useConnectedAuth } from '@context/auth/connected';
import { useMediaQuery } from '@context/mediaQuery';
import { useTieredUpload } from '@hooks/useTieredUpload';
import { trackClickEvent } from '@lib/analytics';
import { useHeaderStore } from '@store/header';
import { BLUR_BACKGROUND_PATHS, FEATURE_GATES } from '@utils/constants';

import { NAV_LINKS } from '@utils/constants';
import { DataTestId } from '@utils/test';
import { HeaderAvatar } from '../Avatar';
import { ConnectWallet } from '../ConnectWallet';
import { navLinkStyle } from '../styles';
import { NavLinks } from './Links';
import { WalletBalance } from './WalletBalance';

const LogOutNavLink = styled('div', navLinkStyle, {
  marginBottom: 4,
});

export const DesktopNav = () => {
  const { pathname } = useRouter();
  const { close, open, areUserOptionsOpen } = useHeaderStore();
  const { isAuthenticatedWithAPI, authUser, isConnectingAPIUser } = useAuth();
  const { logout } = useConnectedAuth();
  const { tablet } = useMediaQuery();
  const isArtistApplicationPage = pathname === NAV_LINKS.ArtistApplication;
  const { value: pointsEnabled } = useGate(FEATURE_GATES.SOCIALS_POINTS);

  const { createDraft, loading } = useTieredUpload();

  const isBlurBackground = BLUR_BACKGROUND_PATHS.includes(pathname);

  const closeMenuAndLogOut = () => {
    trackClickEvent({
      event: 'Logout Clicked',
      properties: {
        sourceComponent: 'Navigation',
      },
    });
    close();
    logout();
  };

  const onClick = () => {
    if (areUserOptionsOpen) {
      return close();
    }
    return open('user');
  };

  const logOutLink = isConnectingAPIUser ? (
    <div className="mb-2 ml-4 block h-8 w-36 animate-pulse rounded-sm bg-neutral200" />
  ) : (
    <LogOutNavLink variant="desktop" onClick={closeMenuAndLogOut}>
      Logout
    </LogOutNavLink>
  );

  const isVerifiedArtist = !!authUser?.artist;

  return (
    <Root className="ml-auto">
      <List className="flex list-none items-center">
        <div className="flex-end ml-4 flex h-10 w-full items-center gap-3">
          {!isAuthenticatedWithAPI ? (
            <ConnectWallet variant="desktop" loading={isConnectingAPIUser} />
          ) : (
            <>
              {!isArtistApplicationPage && !pointsEnabled && (
                <Button
                  label="Upload"
                  variant={isBlurBackground ? 'tertiary' : 'primary'}
                  loading={loading}
                  disabled={loading}
                  onClick={() => {
                    if (isVerifiedArtist) {
                      // This is already handling errors
                      createDraft();
                    } else {
                      Router.push(NAV_LINKS.ArtistApplication);
                    }
                  }}
                  className="h-9 md:h-10"
                  size="S"
                />
              )}

              {pointsEnabled && (
                <AccessibleLink pathnameUri={NAV_LINKS.Leaderboard}>
                  {/* TODO: integrate */}
                  <TotalPointsBadge totalPoints={1800} size="md" />
                </AccessibleLink>
              )}
              {/**
               * The element is always there in DOM since we hide the nav with CSS
               * This ensure that the dropdown does not open when menu options are opened on mobile
               */}
              <DropdownRoot open={areUserOptionsOpen && tablet}>
                <Trigger onClick={onClick}>
                  <HeaderAvatar />
                </Trigger>
                <Portal>
                  <Content
                    className="text-base relative top-2.5 z-header flex h-fit w-[238px] flex-col rounded-md bg-white text-base-l shadow-primary"
                    onPointerDownOutside={close}
                    align="end"
                    data-testid={DataTestId.USER_MENU_ITEMS_CONTAINER}
                  >
                    {authUser && <UserInfo userInfo={authUser} desktopNavHeader />}
                    <Divider orientation="horizontal" className="!mb-0" />
                    <WalletBalance target="desktop" variant="nav-menu" />

                    <NavLinks variant="desktop" />
                    {logOutLink}
                  </Content>
                </Portal>
              </DropdownRoot>
            </>
          )}
        </div>
      </List>
    </Root>
  );
};
